import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Checkbox, Input, Loading, Modal, Select, Tooltip } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowLeft, IconCalendar, IconExternalLink, IconInfoCircle, IconLink, IconPlus, IconSettings, IconX } from '@tabler/icons-react'
import { getDriveURL } from '@/controller/driveHelper.ts'
import DateModal from '@/components/DateModal.tsx'
import { toast } from 'react-toastify'
import { useClient } from '@/stores/useClient.ts'
import { ProfProject } from '@/models/profProject.ts'
import { getThumbnailFromAlbums } from '@/models/album.ts'
import LoadBig from '@/components/LoadBig/LoadBig.tsx'
import ProjectHeader from '@/components/portal/ProjectHeader.tsx'
import PortalTabs, { PortalTab } from '@/components/portal/PortalTabs.tsx'
import AlbumCard from '@/components/portal/AlbumCard.tsx'
import ProjectEmailTab from '@/components/portal/ProjectEmailTab.tsx'
import ProjectDesignTab from '@/components/portal/ProjectDesignTab.tsx'
import ProjectGuestGalleryTab from '@/components/portal/ProjectGuestGalleryTab.tsx'
import ProjectSelectionsTab from '@/components/portal/ProjectSelectionsTab.tsx'
import StatsPanel from '@/components/Stats/StatsPanel.tsx'
import StatHistoryList from '@/components/Stats/StatHistoryList.tsx'
import { useAlbums } from '@/stores/useAlbums.ts'
import { useDrives } from '@/stores/useDrives.ts'
import { usePortalContext } from '@/pages/portal/Portal.page.tsx'
import PriceListSelect from '@/components/portal/PriceListSelect.tsx'
import TagInput from '@/components/portal/TagInput.tsx'
import AutoSaveTextarea from '@/components/portal/AutoSaveTextarea.tsx'
import TagEditDialog, { useTagEditDialog } from '@/components/portal/TagEditDialog.tsx'
import ProjectTemplateSelect, { PROJECT_TEMPLATES_QUERY_KEY, useProjectTemplates } from '@/components/portal/ProjectTemplateSelect.tsx'
import { CreateProjectTemplateModal, useCreateProjectTemplateModal } from '@/components/portal/CreateProjectTemplateModal.tsx'
import { ProjectTemplateManagementModal, useProjectTemplateManagementModal } from '@/components/portal/ProjectTemplateManagementModal.tsx'
import { ConfirmModal, useConfirmModal } from '@/components/portal/ConfirmModal.tsx'
import { diffProjectAgainstTemplate } from '@/models/profProjectTemplate.ts'
import { ProfTag } from '@/models/profTag.ts'
import { OpenedImageCaption } from '@/models/drive.ts'
import { DownloadOptions } from '@/models/downloadOptions.ts'
import DownloadOptionsModal, { downloadOptionsLabel } from '@/components/portal/DownloadOptionsModal.tsx'

const OPENED_IMAGE_CAPTION_OPTIONS: Array<{ value: OpenedImageCaption, label: string }> = [
  { value: '', label: 'Keine Angabe (Standard)' },
  { value: 'filename', label: 'Dateiname' },
  { value: 'date', label: 'Datum' },
  { value: 'filename,date', label: 'Dateiname und Datum' },
]

const projectTabs: PortalTab[] = [
  { id: 'images', label: 'Bilder' },
  { id: 'settings', label: 'Einstellungen' },
  { id: 'design', label: 'Design', spotId: 'design-tab' },
  { id: 'guest-gallery', label: 'Gäste-Galerie' },
  { id: 'selections', label: 'Auswahlen' },
  { id: 'statistics', label: 'Statistiken' },
  { id: 'emails', label: 'Versenden' },
]

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { getProject, createAlbum, updateProject } = useClient()
  const queryClient = useQueryClient()
  const { setAlbum } = useAlbums()
  const { setDrive, setCurrentDrive } = useDrives()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab = projectTabs.some(t => t.id === searchParams.get('tab')) ? searchParams.get('tab')! : 'images'
  const [activeTab, setActiveTab] = useState(initialTab)

  function handleTabChange(tabId: string) {
    setActiveTab(tabId)
    if (tabId === 'images') {
      // keep URL clean for the default tab
      const next = new URLSearchParams(searchParams)
      next.delete('tab')
      setSearchParams(next, { replace: true })
    } else {
      const next = new URLSearchParams(searchParams)
      next.set('tab', tabId)
      setSearchParams(next, { replace: true })
    }
  }
  const [isCreatingAlbum, setIsCreatingAlbum] = useState(false)
  const [albumTitle, setAlbumTitle] = useState('')
  const createAlbumModalRef = useRef<HTMLDialogElement>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const res = await getProject(projectId!)
      return res.data as ProfProject
    },
    enabled: !!projectId,
  })

  // Register drive and albums in the Zustand store so FilesProvider and FilesSlideshow can access them
  useEffect(() => {
    if (data) {
      setDrive(data.drive)
      setCurrentDrive(data.drive)
      for (const album of data.drive.albums) {
        setAlbum(album)
      }
    }
  }, [data, setAlbum, setDrive, setCurrentDrive])

  async function handleTitleChange(newTitle: string) {
    await updateProject(projectId!, { title: newTitle })
    queryClient.invalidateQueries({ queryKey: ['project', projectId] })
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    toast('Projektname aktualisiert', { closeButton: true, autoClose: 2000 })
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(getDriveURL(data!.drive.id))
    toast('Link kopiert', { autoClose: 2000 })
  }

  async function handleCreateAlbum(e: React.FormEvent) {
    e.preventDefault()
    if (!data || !albumTitle.trim()) return
    setIsCreatingAlbum(true)
    try {
      await createAlbum(data.drive.id, { title: albumTitle.trim() })
      setAlbumTitle('')
      createAlbumModalRef.current?.close()
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
    } catch (err: any) {
      console.error('Fehler beim Erstellen des Albums:', err)
      alert('Fehler beim Erstellen des Albums')
    } finally {
      setIsCreatingAlbum(false)
    }
  }

  if (isLoading) return <LoadBig />

  if (error || !data) {
    return (
      <div className="container mx-auto p-4">
        <p className="text-error">Fehler beim Laden des Projekts</p>
      </div>
    )
  }

  const project = data

  return (
    <div className="container mx-auto px-3 md:px-5 py-5">
      <Link to="/portal/projects" className="inline-flex items-center gap-2 text-sm opacity-50 hover:opacity-100 transition-opacity mb-6">
        <IconArrowLeft size={16} />
        Projekte
      </Link>

      <ProjectHeader
        title={project.title}
        albumCount={project.drive.albums.length}
        thumbnail={getThumbnailFromAlbums(project.drive.albums)}
        onTitleChange={handleTitleChange}
        tags={project.tags}
        actions={
          <>
            <Button className="flex-1 md:flex-none" size="sm" color="primary" variant="outline" onClick={handleCopyLink}>
              <IconLink size={16} />
              Link kopieren
            </Button>
            <a className="flex-1 md:flex-none" href={getDriveURL(project.drive.id)} target="_blank" rel="noopener noreferrer">
              <Button className="w-full md:w-auto" size="sm" variant="outline" color="ghost">
                <IconExternalLink size={16} />
                Kunden-Ansicht
              </Button>
            </a>
          </>
        }
      />

      <PortalTabs tabs={projectTabs} activeTab={activeTab} onTabChange={handleTabChange} />

      {activeTab === 'images' && (
        <div>
          <div className="flex flex-col gap-5">
            {project.drive.albums.map(album => (
              <AlbumCard key={album.id} album={album} projectId={projectId!} />
            ))}
          </div>
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={() => {
              setAlbumTitle('')
              createAlbumModalRef.current?.showModal()
            }}>
              <IconPlus size={18} />
              Neues Album
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <ProjectSettingsTab project={project} projectId={projectId!} />
      )}

      {activeTab === 'design' && (
        <ProjectDesignTab
          project={project}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['project', projectId] })}
        />
      )}

      {activeTab === 'guest-gallery' && (
        <ProjectGuestGalleryTab />
      )}

      {activeTab === 'selections' && (
        <ProjectSelectionsTab driveId={project.drive.id} projectId={projectId!} />
      )}

      {activeTab === 'statistics' && (
        // Filtered by project, not by drive: it stays correct if the project is
        // ever assigned a different drive.
        <StatsPanel filters={{ project: projectId! }}>
          <StatHistoryList filters={{ project: projectId! }} />
        </StatsPanel>
      )}

      {activeTab === 'emails' && (
        <ProjectEmailTab project={project} />
      )}

      <Modal ref={createAlbumModalRef} className="bg-neutral">
        <Modal.Header>
          <span className="text-xl font-bold">Neues Album erstellen</span>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleCreateAlbum} className="flex flex-col gap-4">
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Albumname</span>
              </div>
              <Input
                placeholder="z.B. Zeremonie"
                value={albumTitle}
                onChange={e => setAlbumTitle(e.target.value)}
                required
                autoFocus
              />
            </label>
            <div className="flex flex-row gap-3 justify-center">
              <Button type="button" color="ghost" disabled={isCreatingAlbum}
                onClick={() => createAlbumModalRef.current?.close()}>
                Abbrechen
              </Button>
              <Button type="submit" color="primary" loading={isCreatingAlbum} disabled={isCreatingAlbum || !albumTitle.trim()}>
                Erstellen
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  )
}

/**
 * The little "i" next to a setting. The inner span is `flex`, not `inline-flex`: an inline
 * child adds line-box descender space inside the inline-block Tooltip, which pushes the icon
 * off-centre relative to its row.
 */
function SettingInfoIcon({ message }: { message: string }) {
  return (
    <Tooltip message={message} color="secondary">
      <span className="flex items-center cursor-help">
        <IconInfoCircle size={16} className="opacity-50 hover:opacity-100" />
      </span>
    </Tooltip>
  )
}

type DriveFlagField = 'is_hide_selection' | 'is_hide_download' | 'is_hide_share' | 'is_selection_name_required' | 'is_selection_email_required' | 'is_selection_message_required'

function ProjectSettingsTab({ project, projectId }: { project: ProfProject, projectId: string }) {
  const { updateProject, updateDrive, createTag, updateTag, createProjectTemplateFromProject, applyProjectTemplate, captureProjectTemplate } = useClient()
  const queryClient = useQueryClient()
  const portalContext = usePortalContext()
  const allTags = portalContext?.account?.tags ?? []
  const tagEdit = useTagEditDialog()
  const [savingFlag, setSavingFlag] = useState<DriveFlagField | null>(null)
  const [isSavingDownloadOptions, setIsSavingDownloadOptions] = useState(false)

  // --- Templates ---
  const { data: templates } = useProjectTemplates()
  const currentTemplate = templates?.find(t => t.id === project.template) ?? null
  // Deviating setting groups — empty means the project matches its template, and then
  // neither "anwenden" (project ← template) nor "aktualisieren" (template ← project) is offered.
  const templateDiff = currentTemplate ? diffProjectAgainstTemplate(project, currentTemplate) : []
  const isOutOfSync = templateDiff.length > 0
  const createTemplateModal = useCreateProjectTemplateModal()
  const manageTemplatesModal = useProjectTemplateManagementModal()
  const applyConfirmModal = useConfirmModal()
  const updateTemplateConfirmModal = useConfirmModal()
  const propagateConfirmModal = useConfirmModal()
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false)
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false)
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false)
  /** Template that was just updated — drives the "update the existing projects too?" dialog */
  const [propagateTarget, setPropagateTarget] = useState<{ id: string, name: string, otherCount: number } | null>(null)
  /**
   * Template the apply dialog refers to. Kept in state because right after linking a
   * template `project.template` is still the previous one until the refetch lands.
   */
  const [applyTarget, setApplyTarget] = useState<{ id: string, name: string } | null>(null)

  function invalidateTemplateData() {
    queryClient.invalidateQueries({ queryKey: ['project', projectId] })
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    // projects_count of the templates changes when a project is linked/unlinked
    queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATES_QUERY_KEY })
  }

  async function handleTemplateChange(templateId: string | null) {
    try {
      await updateProject(projectId, { template: templateId })
      invalidateTemplateData()
      toast(templateId ? 'Vorlage verknüpft' : 'Vorlage entfernt', { autoClose: 2000 })
      // Linking alone changes no setting — offer to apply it right away, but only
      // if the project actually deviates from the newly linked template.
      const linked = templates?.find(t => t.id === templateId)
      if (linked && diffProjectAgainstTemplate(project, linked).length > 0) {
        setApplyTarget({ id: linked.id, name: linked.name })
        applyConfirmModal.show()
      }
    } catch (e: any) {
      console.error('Error updating project template:', e)
      toast.error('Fehler beim Setzen der Vorlage')
    }
  }

  /** Project ← template: overwrite this project's settings with the template */
  async function handleApplyTemplate() {
    if (!applyTarget) return
    setIsApplyingTemplate(true)
    try {
      await applyProjectTemplate(applyTarget.id, [projectId])
      invalidateTemplateData()
      applyConfirmModal.close()
      setApplyTarget(null)
      toast('Vorlage angewendet', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error applying project template:', e)
      toast.error('Fehler beim Anwenden der Vorlage')
    } finally {
      setIsApplyingTemplate(false)
    }
  }

  /** Number of *other* projects that would inherit changes to the linked template */
  const otherProjectsOnTemplate = currentTemplate ? Math.max(0, (currentTemplate.projects_count ?? 1) - 1) : 0
  const unchangedProjectsSentence = otherProjectsOnTemplate === 0
    ? 'Bestehende Projekte mit dieser Vorlage bleiben unverändert'
    : otherProjectsOnTemplate === 1
      ? 'Das eine weitere Projekt mit dieser Vorlage bleibt unverändert'
      : `Die ${otherProjectsOnTemplate} weiteren Projekte mit dieser Vorlage bleiben unverändert`

  /**
   * Template ← project: store this project's settings in the linked template.
   * Always confirmed first — it rewrites a template that other projects may rely on.
   */
  async function handleUpdateTemplateFromProject() {
    if (!currentTemplate) return
    setIsUpdatingTemplate(true)
    try {
      await captureProjectTemplate(currentTemplate.id, { project: projectId })
      invalidateTemplateData()
      updateTemplateConfirmModal.close()
      // Capturing changed the template only — offer to bring the projects using it along.
      if (otherProjectsOnTemplate > 0) {
        setPropagateTarget({ id: currentTemplate.id, name: currentTemplate.name, otherCount: otherProjectsOnTemplate })
        propagateConfirmModal.show()
      } else {
        toast('Vorlage aktualisiert', { autoClose: 2000 })
      }
    } catch (e: any) {
      console.error('Error updating project template from project:', e)
      toast.error('Fehler beim Aktualisieren der Vorlage')
    } finally {
      setIsUpdatingTemplate(false)
    }
  }

  /** Roll the freshly updated template out to every project linked to it */
  async function handlePropagateTemplate() {
    if (!propagateTarget) return
    setIsApplyingTemplate(true)
    try {
      const res = await applyProjectTemplate(propagateTarget.id)
      invalidateTemplateData()
      propagateConfirmModal.close()
      setPropagateTarget(null)
      const count = res.data.applied_to?.length ?? 0
      toast(count === 1 ? 'Vorlage auf 1 Projekt angewendet' : `Vorlage auf ${count} Projekte angewendet`, { autoClose: 2500 })
    } catch (e: any) {
      console.error('Error applying project template:', e)
      toast.error('Fehler beim Anwenden der Vorlage')
    } finally {
      setIsApplyingTemplate(false)
    }
  }

  async function handleCreateTemplateFromProject(name: string) {
    setIsCreatingTemplate(true)
    try {
      const res = await createProjectTemplateFromProject({ project: projectId, name })
      // The template mirrors this project, so link it right away.
      await updateProject(projectId, { template: res.data.id })
      invalidateTemplateData()
      createTemplateModal.close()
      toast('Vorlage erstellt', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error creating project template:', e)
      toast.error('Fehler beim Erstellen der Vorlage')
    } finally {
      setIsCreatingTemplate(false)
    }
  }

  /** Link a template that was created from within the management modal */
  async function handleTemplateCreatedInModal(templateId: string) {
    try {
      await updateProject(projectId, { template: templateId })
      invalidateTemplateData()
    } catch (e: any) {
      console.error('Error linking new project template:', e)
    }
  }

  async function handlePriceListChange(priceListId: string | null) {
    try {
      await updateProject(projectId, { price_list: priceListId })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast(priceListId ? 'Preisliste zugewiesen' : 'Preisliste entfernt', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error updating project price list:', e)
      toast.error('Fehler beim Aktualisieren der Preisliste')
    }
  }

  async function handleAddTag(tag: ProfTag) {
    const newIds = [...project.tags.map(t => t.id), tag.id]
    try {
      await updateProject(projectId, { tag_ids: newIds })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    } catch (e: any) {
      console.error('Error adding tag:', e)
      toast.error('Fehler beim Hinzufügen des Tags')
    }
  }

  async function handleRemoveTag(tag: ProfTag) {
    const newIds = project.tags.filter(t => t.id !== tag.id).map(t => t.id)
    try {
      await updateProject(projectId, { tag_ids: newIds })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    } catch (e: any) {
      console.error('Error removing tag:', e)
      toast.error('Fehler beim Entfernen des Tags')
    }
  }

  async function handleCreateTag(name: string): Promise<ProfTag> {
    const res = await createTag({ name })
    queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
    return res.data
  }

  async function handleEditTagSave(tagId: string, data: { name: string, color: string }) {
    await updateTag(tagId, data)
    queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
    queryClient.invalidateQueries({ queryKey: ['project', projectId] })
    queryClient.invalidateQueries({ queryKey: ['projects'] })
  }

  const dateModalRef = useRef<HTMLDialogElement>(null)
  const expiresModalRef = useRef<HTMLDialogElement>(null)
  const downloadOptionsModalRef = useRef<HTMLDialogElement>(null)

  function formatDateValue(date: Date | null): string | null {
    return date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : null
  }

  async function handleEventDateChange(date: Date | null) {
    const value = formatDateValue(date)
    try {
      await updateDrive(project.drive.id, { event_date: value })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast('Event-Datum aktualisiert', { closeButton: true, autoClose: 2000 })
    } catch {
      toast.error('Fehler beim Aktualisieren des Event-Datums')
    }
  }

  async function handleExpiresAtChange(date: Date | null) {
    const value = formatDateValue(date)
    try {
      await updateDrive(project.drive.id, { user_defined_expires_at: value })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast('Ablaufdatum aktualisiert', { closeButton: true, autoClose: 2000 })
    } catch {
      toast.error('Fehler beim Aktualisieren des Ablaufdatums')
    }
  }

  async function handleNoteChange(note: string) {
    try {
      await updateProject(projectId, { note })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast('Notiz gespeichert', { autoClose: 1500 })
    } catch (e: any) {
      console.error('Error updating project note:', e)
      toast.error('Fehler beim Speichern der Notiz')
    }
  }

  async function handleOpenedImageCaptionChange(caption: OpenedImageCaption) {
    try {
      await updateDrive(project.drive.id, { opened_image_caption: caption })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast('Bildunterschrift aktualisiert', { autoClose: 2000 })
    } catch {
      toast.error('Fehler beim Speichern der Bildunterschrift')
    }
  }

  async function handleDownloadOptionsSave(downloadOptions: DownloadOptions) {
    setIsSavingDownloadOptions(true)
    try {
      await updateDrive(project.drive.id, { download_options: downloadOptions })
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      // Only close on success — on error the dialog stays open with the draft intact.
      downloadOptionsModalRef.current?.close()
      toast('Download-Optionen aktualisiert', { autoClose: 2000 })
    } catch {
      toast.error('Fehler beim Speichern der Download-Optionen')
    } finally {
      setIsSavingDownloadOptions(false)
    }
  }

  async function handleDriveFlagChange(field: DriveFlagField, value: boolean) {
    setSavingFlag(field)
    try {
      await updateDrive(project.drive.id, { [field]: value })
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] })
    } catch {
      toast.error('Fehler beim Speichern')
    } finally {
      setSavingFlag(null)
    }
  }

  const currentEventDate = project.drive.event_date || null
  const displayDate = currentEventDate
    ? new Date(currentEventDate + 'T00:00:00').toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })
    : null

  const currentExpiresAt = project.drive.user_defined_expires_at || null
  const expiresDisplayDate = currentExpiresAt
    ? new Date(currentExpiresAt + 'T00:00:00').toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })
    : null

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-neutral p-4 border border-[var(--portal-border)]">
        <h3 className="font-semibold mb-4">Termine</h3>
        <div className="flex flex-col gap-5">
          <div>
            <p className="text-xs uppercase tracking-wider opacity-50 font-semibold mb-2">Event-Datum</p>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="outline" color="ghost" onClick={() => dateModalRef.current?.showModal()}>
                <IconCalendar size={16} />
                {displayDate || 'Datum auswählen'}
              </Button>
              {currentEventDate && (
                <button
                  className="btn btn-ghost btn-sm btn-circle"
                  title="Datum entfernen"
                  onClick={() => handleEventDateChange(null)}
                >
                  <IconX size={16} />
                </button>
              )}
            </div>
            <p className="text-xs opacity-50 mt-2">Wird in der Galerie-Kopfzeile angezeigt.</p>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider opacity-50 font-semibold mb-2">Ablaufdatum</p>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="outline" color="ghost" onClick={() => expiresModalRef.current?.showModal()}>
                <IconCalendar size={16} />
                {expiresDisplayDate || 'Datum auswählen'}
              </Button>
              {currentExpiresAt && (
                <button
                  className="btn btn-ghost btn-sm btn-circle"
                  title="Datum entfernen"
                  onClick={() => handleExpiresAtChange(null)}
                >
                  <IconX size={16} />
                </button>
              )}
            </div>
            <p className="text-xs opacity-50 mt-2">Nach diesem Tag ist der Zugriff auf die Galerie gesperrt. Besucher werden gebeten, dich zu kontaktieren.</p>
          </div>
        </div>
      </div>

      <DateModal
        modalRef={dateModalRef}
        date={currentEventDate}
        onSetDate={handleEventDateChange}
      />

      <DateModal
        modalRef={expiresModalRef}
        date={currentExpiresAt}
        onSetDate={handleExpiresAtChange}
      />

      <DownloadOptionsModal
        modalRef={downloadOptionsModalRef}
        value={project.drive.download_options || ''}
        hasWatermark={!!project.drive.watermark}
        isSaving={isSavingDownloadOptions}
        onSave={handleDownloadOptionsSave}
      />

      <div className="bg-neutral p-4 border border-[var(--portal-border)]">
        <h3 className="font-semibold mb-4">Tags</h3>
        <div className="max-w-sm">
          <TagInput
            selectedTags={project.tags}
            availableTags={allTags}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            onCreateTag={handleCreateTag}
            onEditTag={(tag) => tagEdit.open(tag)}
          />
        </div>
      </div>

      <div className="bg-neutral p-4 border border-[var(--portal-border)]">
        <h3 className="font-semibold mb-4">Auswahl-Funktionen</h3>
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-3">
            <label className="form-control flex flex-row items-center gap-3 cursor-pointer">
              <span className="inline-flex items-center justify-center w-6 h-6">
                {savingFlag === 'is_hide_download' ? (
                  <Loading size="xs" />
                ) : (
                  <Checkbox
                    color="primary"
                    checked={!!project.drive.is_hide_download}
                    onChange={() => handleDriveFlagChange('is_hide_download', !project.drive.is_hide_download)}
                  />
                )}
              </span>
              <span className="label-text select-none">Download ausblenden</span>
            </label>
            <div className="flex items-center gap-2">
              <SettingInfoIcon message="Gäste können keine Bilder oder Alben herunterladen." />
              {/* Configuring resolutions is pointless while downloads are off. */}
              {!project.drive.is_hide_download && (
                <button
                  type="button"
                  // Same shape as the info icon next to it, so both align on one line.
                  className="inline-flex items-center"
                  title={`Download-Optionen: ${downloadOptionsLabel(project.drive.download_options)}`}
                  onClick={() => downloadOptionsModalRef.current?.showModal()}
                >
                  <IconSettings size={16} className="opacity-50 hover:opacity-100" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center gap-3">
            <label className="form-control flex flex-row items-center gap-3 cursor-pointer">
              <span className="inline-flex items-center justify-center w-6 h-6">
                {savingFlag === 'is_hide_share' ? (
                  <Loading size="xs" />
                ) : (
                  <Checkbox
                    color="primary"
                    checked={!!project.drive.is_hide_share}
                    onChange={() => handleDriveFlagChange('is_hide_share', !project.drive.is_hide_share)}
                  />
                )}
              </span>
              <span className="label-text select-none">Teilen ausblenden</span>
            </label>
            <SettingInfoIcon message="Gäste können keine ausgewählten Bilder oder Alben teilen." />
          </div>
          <div className="flex flex-row items-center gap-3">
            <label className="form-control flex flex-row items-center gap-3 cursor-pointer">
              <span className="inline-flex items-center justify-center w-6 h-6">
                {savingFlag === 'is_hide_selection' ? (
                  <Loading size="xs" />
                ) : (
                  <Checkbox
                    color="primary"
                    checked={!!project.drive.is_hide_selection}
                    onChange={() => handleDriveFlagChange('is_hide_selection', !project.drive.is_hide_selection)}
                  />
                )}
              </span>
              <span className="label-text select-none">Auswahl ausblenden</span>
            </label>
            <SettingInfoIcon message="Gäste können keine Bilder oder Alben auswählen und an dich senden." />
          </div>
          {!project.drive.is_hide_selection && (
            <div className="ml-3 mt-1 pl-4 border-l border-[var(--portal-border)] flex flex-col gap-2">
              <p className="text-xs uppercase tracking-wider opacity-50 font-semibold">Pflichtfelder bei Auswahl-Absendung</p>
              <div className="flex flex-row items-center gap-3">
                <label className="form-control flex flex-row items-center gap-3 cursor-pointer">
                  <span className="inline-flex items-center justify-center w-6 h-6">
                    {savingFlag === 'is_selection_name_required' ? (
                      <Loading size="xs" />
                    ) : (
                      <Checkbox
                        color="primary"
                        checked={!!project.drive.is_selection_name_required}
                        onChange={() => handleDriveFlagChange('is_selection_name_required', !project.drive.is_selection_name_required)}
                      />
                    )}
                  </span>
                  <span className="label-text select-none">Name erforderlich</span>
                </label>
                <SettingInfoIcon message="Gäste müssen ihren Namen angeben, um eine Auswahl abzusenden." />
              </div>
              <div className="flex flex-row items-center gap-3">
                <label className="form-control flex flex-row items-center gap-3 cursor-pointer">
                  <span className="inline-flex items-center justify-center w-6 h-6">
                    {savingFlag === 'is_selection_email_required' ? (
                      <Loading size="xs" />
                    ) : (
                      <Checkbox
                        color="primary"
                        checked={!!project.drive.is_selection_email_required}
                        onChange={() => handleDriveFlagChange('is_selection_email_required', !project.drive.is_selection_email_required)}
                      />
                    )}
                  </span>
                  <span className="label-text select-none">Email erforderlich</span>
                </label>
                <SettingInfoIcon message="Gäste müssen ihre Email-Adresse angeben, um eine Auswahl abzusenden." />
              </div>
              <div className="flex flex-row items-center gap-3">
                <label className="form-control flex flex-row items-center gap-3 cursor-pointer">
                  <span className="inline-flex items-center justify-center w-6 h-6">
                    {savingFlag === 'is_selection_message_required' ? (
                      <Loading size="xs" />
                    ) : (
                      <Checkbox
                        color="primary"
                        checked={!!project.drive.is_selection_message_required}
                        onChange={() => handleDriveFlagChange('is_selection_message_required', !project.drive.is_selection_message_required)}
                      />
                    )}
                  </span>
                  <span className="label-text select-none">Nachricht erforderlich</span>
                </label>
                <SettingInfoIcon message="Gäste müssen eine Nachricht hinterlassen, um eine Auswahl abzusenden." />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-neutral p-4 border border-[var(--portal-border)]">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-semibold">Wasserzeichen</h3>
          <span className={`badge badge-sm ${project.drive.watermark ? 'badge-success text-[white]' : 'badge-ghost'}`}>
            {project.drive.watermark ? 'Aktiv' : 'Inaktiv'}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex flex-col">
            <p className="text-sm opacity-70">Logo oder Schriftzug über deine Bilder legen.</p>
          </div>
          <Link to={`/portal/projects/${projectId}/watermarks`}>
            <Button size="sm" variant="outline" color="ghost">Verwalten →</Button>
          </Link>
        </div>
      </div>

      <div className="bg-neutral p-4 border border-[var(--portal-border)]">
        <h3 className="font-semibold mb-4">Vorlagen</h3>
        <label className="form-control w-full max-w-sm">
          <div className="label">
            <span className="label-text">Aktuelle Vorlage</span>
          </div>
          <ProjectTemplateSelect
            value={project.template}
            onChange={handleTemplateChange}
            disabled={isApplyingTemplate}
          />
          <div className="label">
            <span className="label-text-alt opacity-60">
              {!currentTemplate && 'Dieses Projekt nutzt keine Vorlage.'}
              {currentTemplate && !isOutOfSync && `Dieses Projekt entspricht der Vorlage „${currentTemplate.name}".`}
              {currentTemplate && isOutOfSync && `Dieses Projekt weicht von „${currentTemplate.name}" ab: ${templateDiff.join(', ')}.`}
            </span>
          </div>
        </label>

        <div className="flex flex-wrap items-center gap-2 mt-2">
          {currentTemplate && isOutOfSync && (
            <>
              <Button size="sm" variant="outline" color="ghost" disabled={isApplyingTemplate || isUpdatingTemplate}
                title="Einstellungen der Vorlage auf dieses Projekt übertragen"
                onClick={() => {
                  setApplyTarget({ id: currentTemplate.id, name: currentTemplate.name })
                  applyConfirmModal.show()
                }}>
                Vorlage anwenden
              </Button>
              <Button size="sm" variant="outline" color="ghost" loading={isUpdatingTemplate}
                disabled={isApplyingTemplate || isUpdatingTemplate}
                title="Einstellungen dieses Projekts in der Vorlage speichern"
                onClick={() => updateTemplateConfirmModal.show()}>
                Vorlage aktualisieren
              </Button>
            </>
          )}
          {!currentTemplate && (
            <Button size="sm" variant="outline" color="ghost" onClick={() => createTemplateModal.show()}>
              <IconPlus size={16} />
              Vorlage aus diesem Projekt erstellen
            </Button>
          )}
          <Button size="sm" variant="outline" color="ghost" onClick={() => manageTemplatesModal.show()}>
            Vorlagen verwalten
          </Button>
        </div>
      </div>

      <div className="bg-neutral p-4 border border-[var(--portal-border)]">
        <h3 className="font-semibold mb-4">Allgemeine Einstellungen</h3>
        <label className="form-control w-full max-w-sm">
          <div className="label">
            <span className="label-text">Bildunterschrift beim Öffnen</span>
          </div>
          <Select
            value={project.drive.opened_image_caption || ''}
            onChange={(e) => handleOpenedImageCaptionChange(e.currentTarget.value as OpenedImageCaption)}
          >
            {OPENED_IMAGE_CAPTION_OPTIONS.map(opt => (
              <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
            ))}
          </Select>
          <div className="label">
            <span className="label-text-alt opacity-60">
              Wird unter einem geöffneten Bild angezeigt. Standard: keine Angabe. Als Datum wird das Aufnahmedatum genutzt, sonst das Upload-Datum.
            </span>
          </div>
        </label>
      </div>

      <div className="bg-neutral p-4 border border-[var(--portal-border)]">
        <h3 className="font-semibold mb-4">Shop-Einstellungen</h3>
        <label className="form-control w-full max-w-sm">
          <div className="label">
            <span className="label-text">Preisliste</span>
          </div>
          <PriceListSelect
            value={project.price_list}
            onChange={handlePriceListChange}
            defaultPriceListId={portalContext?.account?.default_price_list}
          />
          <div className="label">
            <span className="label-text-alt opacity-60">
              Kunden dieses Projekts sehen die Preise dieser Liste im Shop.
            </span>
          </div>
        </label>
      </div>

      <div className="bg-neutral p-4 border border-[var(--portal-border)]">
        <h3 className="font-semibold mb-2">Interne Notiz</h3>
        <div className="max-w-2xl">
          {/* Starts as a single line and grows with the content — usually there is nothing here. */}
          <AutoSaveTextarea
            value={project.note || ''}
            placeholder="z. B. Infos zum Kunden, Absprachen, Termine …"
            onSave={handleNoteChange}
            rows={1}
            autoGrow
          />
          <p className="text-xs opacity-50 mt-2">
            Diese Notiz sehen nur du und niemand sonst. Kunden bekommen sie niemals zu sehen — sie dient allein deiner Orientierung.
          </p>
        </div>
      </div>

      <TagEditDialog
        modalRef={tagEdit.modalRef}
        tag={tagEdit.tag}
        onSave={handleEditTagSave}
      />

      <CreateProjectTemplateModal
        modalRef={createTemplateModal.modalRef}
        defaultName={project.title}
        onConfirm={handleCreateTemplateFromProject}
        isLoading={isCreatingTemplate}
      />

      <ProjectTemplateManagementModal
        modalRef={manageTemplatesModal.modalRef}
        captureProject={{ id: projectId, title: project.title }}
        onChanged={invalidateTemplateData}
        onTemplateCreated={handleTemplateCreatedInModal}
      />

      <ConfirmModal
        modalRef={applyConfirmModal.modalRef}
        title="Vorlage anwenden"
        message={applyTarget
          ? `Die Einstellungen dieses Projekts werden mit „${applyTarget.name}" überschrieben.`
          : ''}
        confirmLabel="Anwenden"
        cancelLabel="Später"
        isLoading={isApplyingTemplate}
        onConfirm={handleApplyTemplate}
        onCancel={() => setApplyTarget(null)}
      />

      <ConfirmModal
        modalRef={updateTemplateConfirmModal.modalRef}
        title="Vorlage aktualisieren"
        message={currentTemplate
          ? `„${currentTemplate.name}" wird mit den Einstellungen dieses Projekts überschrieben. ${unchangedProjectsSentence} — danach kannst du sie auf den neuen Stand bringen.`
          : ''}
        confirmLabel="Vorlage aktualisieren"
        isLoading={isUpdatingTemplate}
        onConfirm={handleUpdateTemplateFromProject}
      />

      <ConfirmModal
        modalRef={propagateConfirmModal.modalRef}
        title="Existierende Projekte aktualisieren?"
        message={propagateTarget
          ? `„${propagateTarget.name}" wurde aktualisiert. ${propagateTarget.otherCount === 1
            ? 'Das eine weitere Projekt mit dieser Vorlage'
            : `Die ${propagateTarget.otherCount} weiteren Projekte mit dieser Vorlage`} ${propagateTarget.otherCount === 1 ? 'ist' : 'sind'} noch auf dem alten Stand. Jetzt aktualisieren?`
          : ''}
        confirmLabel="Projekte aktualisieren"
        cancelLabel="Später"
        isLoading={isApplyingTemplate}
        onConfirm={handlePropagateTemplate}
        onCancel={() => setPropagateTarget(null)}
      />
    </div>
  )
}
